import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Grid,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  FormControlLabel,
  Checkbox,
  Chip,
  Autocomplete,
  Tabs,
  Tab,
  InputAdornment
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  Favorite as FavoriteIcon,
  Person as PersonIcon,
  Work as WorkIcon,
  Settings as SettingsIcon,
  Language as WebsiteIcon,
  Instagram as InstagramIcon,
  Facebook as FacebookIcon,
  LinkedIn as LinkedInIcon,
  Phone as PhoneIcon,
  LocalHospital as EmergencyIcon,
  Add as AddIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import IconButton from '@mui/material/IconButton';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useProfile } from '../contexts/ProfileContext';
import FamilyCodeManager from './FamilyCodeManager';
import PendingInvites from './PendingInvites';
import ProfessionalProfileForm from './ProfessionalProfileForm';
import { invitationService } from '../services/invitationService';
import { accessRequestService } from '../services/accessRequestService';
import { subscriptionService } from '../services/subscriptionService';
import { sendEmailNotification, EMAIL_TEMPLATES } from '../services/notifications';
import { db } from '../firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import logger from '../utils/logger';

function Profile() {
  const { currentUser } = useAuth();
  const { profile, loading, error: profileError, updateProfile } = useProfile();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [formData, setFormData] = useState({
    firstName: profile?.firstName || '',
    lastName: profile?.lastName || '',
    email: currentUser?.email || '',
    username: profile?.username || '',
    phoneNumber: profile?.phoneNumber || '',
    displayName: profile?.displayName || '',
    bio: profile?.bio || '',
    website: profile?.website || '',
    instagram: profile?.instagram || '',
    facebook: profile?.facebook || '',
    linkedIn: profile?.linkedIn || '',
    twitter: profile?.twitter || '',
    birthday: profile?.birthday || '',
    anniversaryDate: profile?.anniversaryDate || '',
    spouseId: profile?.spouseId || '',
    spouseName: profile?.spouseName || ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [familyMembers, setFamilyMembers] = useState([]);

  // Invitation dialog state
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteFormData, setInviteFormData] = useState({
    email: '',
    relationship: 'sibling',
    firstName: '',
    lastName: '',
    customMessage: ''
  });
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [inviteSuccess, setInviteSuccess] = useState('');
  const [inviteLink, setInviteLink] = useState('');
  const [sponsorSeat, setSponsorSeat] = useState(false);
  const [availableSeats, setAvailableSeats] = useState(0);
  const [activeTab, setActiveTab] = useState(0);
  const [professionalLoading, setProfessionalLoading] = useState(false);

  // Join Another Family state
  const [joinFamilyCode, setJoinFamilyCode] = useState('');
  const [joinLoading, setJoinLoading] = useState(false);
  const [joinError, setJoinError] = useState('');
  const [joinSuccess, setJoinSuccess] = useState('');
  const [pendingJoinRequests, setPendingJoinRequests] = useState([]);

  // Emergency Contacts state
  const [emergencyContacts, setEmergencyContacts] = useState([]);
  const [emergencyLoading, setEmergencyLoading] = useState(false);
  const [emergencySuccess, setEmergencySuccess] = useState('');

  const relationshipOptions = [
    // Immediate Family
    { value: 'spouse', label: 'Spouse' },
    { value: 'partner', label: 'Partner' },
    { value: 'child', label: 'Child' },
    { value: 'parent', label: 'Parent' },
    { value: 'sibling', label: 'Sibling' },
    // Extended Family
    { value: 'sibling-child', label: 'Sibling\'s Child' },
    { value: 'grandparent', label: 'Grandparent' },
    { value: 'grandchild', label: 'Grandchild' },
    { value: 'aunt/uncle', label: 'Aunt/Uncle' },
    { value: 'niece/nephew', label: 'Niece/Nephew' },
    { value: 'cousin', label: 'Cousin' },
    // Non-family/Advisory
    { value: 'friend', label: 'Friend' },
    { value: 'caregiver', label: 'Caregiver' },
    { value: 'power-of-attorney', label: 'Power of Attorney' },
    { value: 'executor', label: 'Executor' },
    { value: 'trustee', label: 'Trustee' },
    { value: 'advisor', label: 'Advisor' },
    { value: 'attorney', label: 'Attorney' },
    { value: 'accountant', label: 'Accountant' },
    { value: 'financial-planner', label: 'Financial Planner' },
    { value: 'doctor', label: 'Doctor' },
    { value: 'emergency-contact', label: 'Emergency Contact' },
    { value: 'other', label: 'Other' }
  ];

  // Update local state when profile changes
  useEffect(() => {
    if (profile) {
      setFormData(prev => ({
        ...prev,
        firstName: profile.firstName || '',
        lastName: profile.lastName || '',
        username: profile.username || '',
        phoneNumber: profile.phoneNumber || '',
        displayName: profile.displayName || '',
        bio: profile.bio || '',
        website: profile.website || '',
        instagram: profile.instagram || '',
        facebook: profile.facebook || '',
        linkedIn: profile.linkedIn || '',
        twitter: profile.twitter || '',
        birthday: profile.birthday || '',
        anniversaryDate: profile.anniversaryDate || '',
        spouseId: profile.spouseId || '',
        spouseName: profile.spouseName || ''
      }));
      setEmergencyContacts(profile.emergencyContacts || []);
    }
  }, [profile]);

  // Fetch family members for spouse selection
  useEffect(() => {
    const fetchFamilyMembers = async () => {
      if (!profile?.familyCode) return;

      try {
        const usersRef = collection(db, 'users');
        const q = query(usersRef, where('familyCode', '==', profile.familyCode));
        const snapshot = await getDocs(q);

        const members = snapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data()
          }))
          .filter(member => member.id !== currentUser?.uid); // Exclude self

        setFamilyMembers(members);
      } catch (err) {
        logger.error('Error fetching family members:', err);
      }
    };

    fetchFamilyMembers();
  }, [profile?.familyCode, currentUser?.uid]);

  // Handle family code changes from FamilyCodeManager
  const handleFamilyCodeChange = useCallback(async (event) => {
    try {
      const { familyCode } = event.detail;
      const now = new Date().toISOString();
      
      await updateProfile({
        ...profile,
        familyCode,
        role: familyCode ? (profile.role || 'member') : 'member', // Reset role to member when leaving family
        updatedAt: now
      });
      
      setSuccess('Family code updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
      setError('');
    } catch (err) {
      setError(err.message);
      setSuccess('');
    }
  }, [profile, updateProfile]);

  // Handle errors from FamilyCodeManager
  const handleFamilyCodeError = useCallback((event) => {
    setError(event.detail.message);
    setSuccess('');
  }, []);

  // Add event listeners for family code changes
  useEffect(() => {
    const familyCodeManager = document.querySelector('[data-testid="family-code-manager"]');
    if (!familyCodeManager) return;

    const onFamilyCodeChange = (event) => handleFamilyCodeChange(event);
    const onError = (event) => handleFamilyCodeError(event);

    familyCodeManager.addEventListener('familyCodeChange', onFamilyCodeChange);
    familyCodeManager.addEventListener('error', onError);
    
    return () => {
      familyCodeManager.removeEventListener('familyCodeChange', onFamilyCodeChange);
      familyCodeManager.removeEventListener('error', onError);
    };
  }, [handleFamilyCodeChange, handleFamilyCodeError]);

  const handleInviteDialogOpen = async () => {
    setInviteDialogOpen(true);
    setInviteError('');
    setInviteSuccess('');
    setInviteLink('');
    setSponsorSeat(false);
    setInviteFormData({ email: '', relationship: 'sibling', firstName: '', lastName: '', customMessage: '' });
    // Load seat availability
    try {
      const seats = await subscriptionService.getSeatUsage(profile?.familyCode);
      const avail = seats ? Math.max(0, (seats.totalSeats || 0) - (seats.usedSeats || 0)) : 0;
      setAvailableSeats(avail);
    } catch {
      setAvailableSeats(0);
    }
  };

  const handleInviteDialogClose = () => {
    setInviteDialogOpen(false);
    setInviteFormData({ email: '', relationship: 'sibling', firstName: '', lastName: '', customMessage: '' });
    setInviteError('');
    setInviteSuccess('');
    setInviteLink('');
    setSponsorSeat(false);
    setAvailableSeats(0);
  };

  const handleInviteSubmit = async (e) => {
    e.preventDefault();
    setInviteError('');
    setInviteSuccess('');
    setInviteLink('');
    setInviteLoading(true);

    try {
      if (!profile?.familyCode) {
        throw new Error('You must be part of a family to send invites');
      }

      if (!inviteFormData.email) {
        throw new Error('Please enter an email address');
      }

      if (!inviteFormData.relationship) {
        throw new Error('Please select a relationship');
      }

      // Create recipient name from first/last name if provided
      const inviteeName = inviteFormData.firstName || inviteFormData.lastName
        ? `${inviteFormData.firstName} ${inviteFormData.lastName}`.trim()
        : null;

      // Send invite using new invitationService with auto-group assignment
      const invitation = await invitationService.createInvitation(
        profile.familyCode,
        profile.uid,
        {
          inviteeEmail: inviteFormData.email,
          inviteeName: inviteeName,
          relationship: inviteFormData.relationship,
          customMessage: inviteFormData.customMessage,
          sponsorSeat: sponsorSeat && availableSeats > 0,
        }
      );

      // Send email notification with the invite link
      try {
        await sendEmailNotification(EMAIL_TEMPLATES.PENDING_APPROVAL, {
          to: inviteFormData.email,
          inviterName: `${profile.firstName} ${profile.lastName}`,
          inviteeName: inviteeName || inviteFormData.email,
          familyCode: profile.familyCode,
          relationship: inviteFormData.relationship,
          customMessage: inviteFormData.customMessage || '',
          acceptUrl: invitation.inviteLink
        });
      } catch (emailErr) {
        // Email failed but invite was created — show the link so they can share manually
        logger.error('Invite email failed:', emailErr);
      }

      setInviteSuccess(`Invitation sent to ${inviteFormData.email}! Groups will be automatically assigned based on relationship.`);
      setInviteLink(invitation.inviteLink);

      // Reset form after a short delay
      setTimeout(() => {
        setInviteFormData({ email: '', relationship: 'sibling', firstName: '', lastName: '', customMessage: '' });
      }, 3000);
    } catch (err) {
      setInviteError(err.message || 'Failed to send invitation');
    } finally {
      setInviteLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const now = new Date().toISOString();
      let createdAt = now;
      
      // Try to parse existing createdAt, fallback to now if invalid
      if (profile.createdAt) {
        const parsedDate = new Date(profile.createdAt);
        // Check if the date is valid and not too far in the past or future
        if (!isNaN(parsedDate) && 
            parsedDate.getTime() > new Date('2000-01-01').getTime() && 
            parsedDate.getTime() < new Date('2100-01-01').getTime()) {
          createdAt = parsedDate.toISOString();
        }
      }

      await updateProfile({
        uid: currentUser.uid,
        email: currentUser.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        username: formData.username,
        phoneNumber: formData.phoneNumber,
        displayName: formData.displayName,
        bio: formData.bio,
        website: formData.website || '',
        instagram: formData.instagram || '',
        facebook: formData.facebook || '',
        linkedIn: formData.linkedIn || '',
        twitter: formData.twitter || '',
        birthday: formData.birthday || null,
        anniversaryDate: formData.anniversaryDate || null,
        spouseId: formData.spouseId || null,
        spouseName: formData.spouseName || null,
        familyCode: profile.familyCode,
        role: profile.role || 'member',
        isActive: true,
        createdAt: createdAt,
        updatedAt: now
      });
      setSuccess('Profile updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
      setError('');
    } catch (err) {
      setError(err.message);
      setSuccess('');
    }
  };

  const handleSaveProfessionalProfile = async (data) => {
    setProfessionalLoading(true);
    setError('');
    try {
      const now = new Date().toISOString();
      await updateProfile({
        ...profile,
        professionalProfile: data.professionalProfile,
        businessInfo: data.businessInfo,
        updatedAt: now
      });
      setSuccess('Professional profile updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
      setSuccess('');
    } finally {
      setProfessionalLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleJoinAnotherFamily = async () => {
    if (!joinFamilyCode.trim()) return;
    setJoinLoading(true);
    setJoinError('');
    setJoinSuccess('');
    try {
      const userName = profile.displayName || `${profile.firstName} ${profile.lastName}`.trim() || currentUser.email;
      await accessRequestService.createFamilyJoinRequest(
        currentUser.uid,
        joinFamilyCode.trim().toUpperCase(),
        currentUser.email,
        userName
      );
      setJoinSuccess(`Request sent! The admins of family ${joinFamilyCode.trim().toUpperCase()} will review your request.`);
      setPendingJoinRequests(prev => [...prev, { familyCode: joinFamilyCode.trim().toUpperCase(), status: 'pending', createdAt: new Date() }]);
      setJoinFamilyCode('');
    } catch (err) {
      setJoinError(err.message || 'Failed to send join request');
    } finally {
      setJoinLoading(false);
    }
  };

  // ── Emergency Contacts ─────────────────────────────────────────────────────

  const addEmergencyContact = () => {
    setEmergencyContacts(prev => [...prev, { name: '', phone: '', email: '', relationship: '' }]);
  };

  const updateEmergencyContact = (index, field, value) => {
    setEmergencyContacts(prev => prev.map((c, i) => i === index ? { ...c, [field]: value } : c));
  };

  // Bulk-update multiple fields at once (used when a family member is selected from autocomplete)
  const updateEmergencyContactBulk = (index, fields) => {
    setEmergencyContacts(prev => prev.map((c, i) => i === index ? { ...c, ...fields } : c));
  };

  const removeEmergencyContact = (index) => {
    setEmergencyContacts(prev => prev.filter((_, i) => i !== index));
  };

  const handleSaveEmergencyContacts = async () => {
    setEmergencyLoading(true);
    setEmergencySuccess('');
    try {
      await updateProfile({ emergencyContacts });
      setEmergencySuccess('Emergency contacts saved.');
      setTimeout(() => setEmergencySuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setEmergencyLoading(false);
    }
  };

  if (!currentUser) {
    return (
      <Box p={3}>
        <Typography variant="h4" gutterBottom>
          Profile
        </Typography>
        <Paper sx={{ p: 3, maxWidth: 800 }}>
          <Alert severity="error">Please sign in to view your profile</Alert>
        </Paper>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box p={3}>
        <Typography variant="h4" gutterBottom>
          Profile
        </Typography>
        <Paper sx={{ p: 3, maxWidth: 800, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress role="progressbar" />
        </Paper>
      </Box>
    );
  }

  if (!profile) {
    return (
      <Box p={3}>
        <Typography variant="h4" gutterBottom>
          Profile
        </Typography>
        <Paper sx={{ p: 3, maxWidth: 800 }}>
          <Alert severity="error">Profile not found. Please try refreshing the page.</Alert>
        </Paper>
      </Box>
    );
  }

  if (!profile.familyCode) {
    return (
      <Box p={3}>
        <Typography variant="h4" gutterBottom>
          Profile Setup
        </Typography>
        <Paper sx={{ p: 3, maxWidth: 800 }}>
          <Typography variant="body1" gutterBottom>
            Welcome! To get started, you need to either create a new family or join an existing one.
          </Typography>
          <Box mt={2}>
            <FamilyCodeManager />
          </Box>
        </Paper>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        Profile
      </Typography>

      <Paper sx={{ p: 3, maxWidth: 900 }}>
        {(error || profileError) && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error || profileError}
          </Alert>
        )}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        {/* Profile Tabs */}
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab
            icon={<PersonIcon />}
            iconPosition={isMobile ? 'top' : 'start'}
            label={isMobile ? undefined : 'Personal Info'}
            aria-label="Personal Info"
          />
          <Tab
            icon={<WorkIcon />}
            iconPosition={isMobile ? 'top' : 'start'}
            label={isMobile ? undefined : 'Professional Profile'}
            aria-label="Professional Profile"
          />
          <Tab
            icon={<EmergencyIcon />}
            iconPosition={isMobile ? 'top' : 'start'}
            label={isMobile ? undefined : 'Emergency Contacts'}
            aria-label="Emergency Contacts"
          />
          <Tab
            icon={<SettingsIcon />}
            iconPosition={isMobile ? 'top' : 'start'}
            label={isMobile ? undefined : 'Account Settings'}
            aria-label="Account Settings"
            onClick={() => navigate('/settings')}
          />
        </Tabs>

        {/* Personal Information Tab */}
        {activeTab === 0 && (
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>Basic Information</Typography>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="First Name"
                fullWidth
                value={formData.firstName}
                onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                required
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="Last Name"
                fullWidth
                value={formData.lastName}
                onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>Contact Information</Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Username"
                fullWidth
                value={formData.username}
                onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value }))}
                helperText="Choose a unique username for easier identification"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Display Name"
                fullWidth
                value={formData.displayName}
                onChange={(e) => setFormData(prev => ({ ...prev, displayName: e.target.value }))}
                helperText="Name shown to other family members"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Email"
                fullWidth
                value={formData.email}
                disabled
                helperText="Email cannot be changed as it is tied to your account"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Phone Number"
                fullWidth
                value={formData.phoneNumber}
                onChange={(e) => setFormData(prev => ({ ...prev, phoneNumber: e.target.value }))}
                helperText="For notifications and two-factor authentication"
                InputProps={{
                  startAdornment: <InputAdornment position="start"><PhoneIcon fontSize="small" color="action" /></InputAdornment>
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>Social & Online Presence</Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Website"
                fullWidth
                value={formData.website}
                onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
                placeholder="https://yourwebsite.com"
                InputProps={{
                  startAdornment: <InputAdornment position="start"><WebsiteIcon fontSize="small" color="action" /></InputAdornment>
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="LinkedIn"
                fullWidth
                value={formData.linkedIn}
                onChange={(e) => setFormData(prev => ({ ...prev, linkedIn: e.target.value }))}
                placeholder="https://linkedin.com/in/yourprofile"
                InputProps={{
                  startAdornment: <InputAdornment position="start"><LinkedInIcon fontSize="small" color="action" /></InputAdornment>
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Twitter / X"
                fullWidth
                value={formData.twitter}
                onChange={(e) => setFormData(prev => ({ ...prev, twitter: e.target.value }))}
                placeholder="@username"
                helperText="Your Twitter/X handle"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Instagram"
                fullWidth
                value={formData.instagram}
                onChange={(e) => setFormData(prev => ({ ...prev, instagram: e.target.value }))}
                placeholder="@username"
                InputProps={{
                  startAdornment: <InputAdornment position="start"><InstagramIcon fontSize="small" color="action" /></InputAdornment>
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Facebook"
                fullWidth
                value={formData.facebook}
                onChange={(e) => setFormData(prev => ({ ...prev, facebook: e.target.value }))}
                placeholder="facebook.com/yourprofile"
                InputProps={{
                  startAdornment: <InputAdornment position="start"><FacebookIcon fontSize="small" color="action" /></InputAdornment>
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>Important Dates</Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Birthday"
                type="date"
                fullWidth
                value={formData.birthday || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, birthday: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                helperText="Your birthday (for birthday reminders)"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Anniversary Date"
                type="date"
                fullWidth
                value={formData.anniversaryDate || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, anniversaryDate: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                helperText="Wedding or special anniversary date"
              />
            </Grid>

            {/* Spouse Linking */}
            {formData.anniversaryDate && familyMembers.length > 0 && (
              <Grid item xs={12}>
                <Autocomplete
                  options={familyMembers}
                  getOptionLabel={(option) => option.displayName || `${option.firstName} ${option.lastName}` || option.email || 'Unknown'}
                  value={familyMembers.find(m => m.id === formData.spouseId) || null}
                  onChange={(event, newValue) => {
                    setFormData(prev => ({
                      ...prev,
                      spouseId: newValue?.id || '',
                      spouseName: newValue ? (newValue.displayName || `${newValue.firstName} ${newValue.lastName}`) : ''
                    }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Link Anniversary to Spouse/Partner"
                      placeholder="Select your spouse or partner"
                      helperText="Link your anniversary to show as a shared celebration (e.g., 'John & Jane's Anniversary')"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <FavoriteIcon sx={{ color: 'error.main', mr: 1 }} />
                            {params.InputProps.startAdornment}
                          </>
                        )
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <FavoriteIcon sx={{ color: 'error.light', fontSize: 18 }} />
                        <Typography>
                          {option.displayName || `${option.firstName} ${option.lastName}` || option.email}
                        </Typography>
                      </Box>
                    </li>
                  )}
                />
                {formData.spouseId && (
                  <Chip
                    icon={<FavoriteIcon />}
                    label={`Linked to ${formData.spouseName}`}
                    color="error"
                    variant="outlined"
                    onDelete={() => setFormData(prev => ({ ...prev, spouseId: '', spouseName: '' }))}
                    sx={{ mt: 1 }}
                  />
                )}
              </Grid>
            )}

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>Additional Information</Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Bio"
                fullWidth
                multiline
                rows={4}
                value={formData.bio}
                onChange={(e) => setFormData(prev => ({ ...prev, bio: e.target.value }))}
                helperText="Tell your family members about yourself"
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                >
                  Update Profile
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
        )}

        {/* Professional Profile Tab */}
        {activeTab === 1 && (
          <ProfessionalProfileForm
            professionalProfile={profile?.professionalProfile || {}}
            businessInfo={profile?.businessInfo || {}}
            onSave={handleSaveProfessionalProfile}
            loading={professionalLoading}
          />
        )}

        {/* Emergency Contacts Tab */}
        {activeTab === 2 && (
          <Box>
            <Typography variant="h6" gutterBottom>Emergency Contacts</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              These contacts are shown when you send an Emergency SOS alert.
            </Typography>

            {emergencySuccess && (
              <Alert severity="success" sx={{ mb: 2 }}>{emergencySuccess}</Alert>
            )}

            {emergencyContacts.map((contact, i) => (
              <Paper key={i} variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                  <Typography variant="subtitle2" color="text.secondary">Contact {i + 1}</Typography>
                  <IconButton size="small" onClick={() => removeEmergencyContact(i)} color="error">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={6}>
                    {/* Name — autocomplete searches existing family members; freeSolo allows new entries */}
                    <Autocomplete
                      freeSolo
                      size="small"
                      options={familyMembers}
                      getOptionLabel={m =>
                        typeof m === 'string'
                          ? m
                          : ((m.firstName || '') + ' ' + (m.lastName || '')).trim() || m.displayName || m.email || ''
                      }
                      filterOptions={(options, { inputValue }) => {
                        const lower = inputValue.toLowerCase();
                        return options.filter(m => {
                          const name = ((m.firstName || '') + ' ' + (m.lastName || '')).trim() || m.displayName || '';
                          return name.toLowerCase().includes(lower) || (m.email || '').toLowerCase().includes(lower);
                        });
                      }}
                      inputValue={contact.name}
                      onInputChange={(_, value) => updateEmergencyContact(i, 'name', value)}
                      onChange={(_, selected) => {
                        if (selected && typeof selected === 'object') {
                          const name = ((selected.firstName || '') + ' ' + (selected.lastName || '')).trim()
                            || selected.displayName || selected.email || '';
                          updateEmergencyContactBulk(i, {
                            name,
                            phone: selected.phone || contact.phone,
                            email: selected.email || '',
                          });
                        }
                      }}
                      renderOption={(props, m) => {
                        const name = ((m.firstName || '') + ' ' + (m.lastName || '')).trim() || m.displayName || m.email;
                        return (
                          <li {...props} key={m.id}>
                            <Box>
                              <Typography variant="body2" fontWeight={600}>{name}</Typography>
                              {m.email && <Typography variant="caption" color="text.secondary">{m.email}</Typography>}
                            </Box>
                          </li>
                        );
                      }}
                      renderInput={(params) => (
                        <TextField {...params} label="Name" placeholder="Search family or enter new" fullWidth />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Phone"
                      size="small"
                      type="tel"
                      value={contact.phone}
                      onChange={e => updateEmergencyContact(i, 'phone', e.target.value)}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Email (optional)"
                      size="small"
                      type="email"
                      value={contact.email || ''}
                      onChange={e => updateEmergencyContact(i, 'email', e.target.value)}
                      helperText="Fallback when SMS is blocked"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Relationship"
                      size="small"
                      value={contact.relationship}
                      onChange={e => updateEmergencyContact(i, 'relationship', e.target.value)}
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </Paper>
            ))}

            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={addEmergencyContact}
                size="small"
              >
                Add Contact
              </Button>
              <Button
                variant="contained"
                onClick={handleSaveEmergencyContacts}
                disabled={emergencyLoading}
                size="small"
              >
                {emergencyLoading ? 'Saving...' : 'Save Contacts'}
              </Button>
            </Box>
          </Box>
        )}
      </Paper>

      {/* Family Management Section */}
      <Box mt={4}>
        <Typography variant="h5" gutterBottom>
          Family Management
        </Typography>

        <Paper sx={{ p: 3, mb: 3 }}>
          <FamilyCodeManager />
        </Paper>

        {/* Invite Members Section */}
        {profile?.familyCode && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Typography variant="h6" gutterBottom>
                  Invite Family Members
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Send invitations to family members to join your family board
                </Typography>
              </Box>
              <Button
                variant="contained"
                color="primary"
                startIcon={<PersonAddIcon />}
                onClick={handleInviteDialogOpen}
              >
                Send Invite
              </Button>
            </Box>
          </Paper>
        )}

        {/* Join Another Family Section */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Join Another Family</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter a family code to request access to a second family. The family admins will review and approve your request.
          </Typography>
          {joinError && <Alert severity="error" sx={{ mb: 2 }}>{joinError}</Alert>}
          {joinSuccess && <Alert severity="success" sx={{ mb: 2 }}>{joinSuccess}</Alert>}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <TextField
              label="Family Code"
              value={joinFamilyCode}
              onChange={(e) => setJoinFamilyCode(e.target.value.toUpperCase())}
              placeholder="e.g., SMIT0001"
              size="small"
              sx={{ flexGrow: 1, maxWidth: 300 }}
              disabled={joinLoading}
            />
            <Button
              variant="contained"
              onClick={handleJoinAnotherFamily}
              disabled={joinLoading || !joinFamilyCode.trim()}
              startIcon={joinLoading ? <CircularProgress size={16} /> : <PersonAddIcon />}
            >
              {joinLoading ? 'Sending...' : 'Request to Join'}
            </Button>
          </Box>
          {pendingJoinRequests.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>Pending Requests</Typography>
              {pendingJoinRequests.map((req, i) => (
                <Chip
                  key={i}
                  label={`${req.familyCode} — pending admin approval`}
                  variant="outlined"
                  color="warning"
                  sx={{ mr: 1, mb: 1 }}
                />
              ))}
            </Box>
          )}
        </Paper>

        {/* Show PendingInvites for all users */}
        <PendingInvites />
      </Box>

      {/* Invite Member Dialog */}
      <Dialog open={inviteDialogOpen} onClose={handleInviteDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>Invite Family Member</DialogTitle>
        <form onSubmit={handleInviteSubmit}>
          <DialogContent>
            {inviteError && <Alert severity="error" sx={{ mb: 2 }}>{inviteError}</Alert>}
            {inviteSuccess && <Alert severity="success" sx={{ mb: 2 }}>{inviteSuccess}</Alert>}

            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={inviteFormData.email}
              onChange={(e) => setInviteFormData({ ...inviteFormData, email: e.target.value })}
              disabled={inviteLoading}
              required
              sx={{ mb: 2 }}
              helperText="Enter the email address of the person you want to invite"
            />

            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="First Name (Optional)"
                  value={inviteFormData.firstName}
                  onChange={(e) => setInviteFormData({ ...inviteFormData, firstName: e.target.value })}
                  disabled={inviteLoading}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Last Name (Optional)"
                  value={inviteFormData.lastName}
                  onChange={(e) => setInviteFormData({ ...inviteFormData, lastName: e.target.value })}
                  disabled={inviteLoading}
                />
              </Grid>
            </Grid>

            <FormControl fullWidth required sx={{ mb: 2 }}>
              <InputLabel>Relationship to You</InputLabel>
              <Select
                value={inviteFormData.relationship}
                onChange={(e) => setInviteFormData({ ...inviteFormData, relationship: e.target.value })}
                disabled={inviteLoading}
                label="Relationship to You"
              >
                {relationshipOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                Groups and permissions will be automatically assigned based on this relationship
              </FormHelperText>
            </FormControl>

            {/* Sponsor seat option — only shown when owner has available seats */}
            {availableSeats > 0 && (
              <FormControlLabel
                sx={{ mb: 2 }}
                control={
                  <Checkbox
                    checked={sponsorSeat}
                    onChange={(e) => setSponsorSeat(e.target.checked)}
                    disabled={inviteLoading}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      Cover this member with one of your seats ({availableSeats} remaining)
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Their access is included in your plan — they won't need their own subscription.
                    </Typography>
                  </Box>
                }
              />
            )}
            {availableSeats === 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                No seats available. The invitee will receive a 14-day free trial and then be prompted to subscribe at $7.99/mo.
              </Alert>
            )}

            <TextField
              fullWidth
              label="Custom Message (Optional)"
              multiline
              rows={3}
              value={inviteFormData.customMessage}
              onChange={(e) => setInviteFormData({ ...inviteFormData, customMessage: e.target.value })}
              disabled={inviteLoading}
              placeholder="Add a personal message to the invitation..."
              helperText="This message will be included in the invitation"
            />

            {inviteLink && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'success.lighter', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Invitation Link Created:
                </Typography>
                <TextField
                  fullWidth
                  value={inviteLink}
                  InputProps={{
                    readOnly: true,
                  }}
                  size="small"
                  onClick={(e) => e.target.select()}
                  helperText="Click to select and copy the link"
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleInviteDialogClose} disabled={inviteLoading}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={inviteLoading || !inviteFormData.email}>
              {inviteLoading ? 'Sending...' : 'Send Invitation'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
}

export default Profile; 